import { Stack, TooltipHost, Text, IconButton } from "@fluentui/react";
import { Sparkline } from "@fluentui/react-charting";

export interface CustomMetricsCardProps {
  title: string;
  description: string;
  currentValue: number | string;
  abbrevatedValue?: string;
  color: string;
  sparklineData?: {
    chartTitle: string;
    lineChartData: {
      legend: string;
      color: string;
      data: { x: number; y: number }[];
    }[];
  };
  disableSparkline?: boolean;
  showTooltipIcon?: boolean;
  onClick?: () => void;
};

export const CustomMetricsCard = ({
  title,
  description,
  currentValue,
  abbrevatedValue,
  color,
  sparklineData,
  disableSparkline = true,
  showTooltipIcon = false,
  onClick
}: CustomMetricsCardProps) => {
  const sampleData = sparklineData || {
    chartTitle: abbrevatedValue ?? String(currentValue),
    lineChartData: [
      {
        legend: abbrevatedValue ?? String(currentValue),
        color: color,
        data: [
          { x: 1, y: 58.13 },
          { x: 2, y: 140.98 },
          { x: 3, y: 20 },
          { x: 4, y: 89.7 },
          { x: 5, y: 99 },
          { x: 6, y: 13.28 },
          { x: 7, y: 31.32 },
          { x: 8, y: 89.7 },
        ],
      },
    ],
  };

  return (
    <TooltipHost
      content={
        <Stack tokens={{ childrenGap: 2 }}>
          <Text variant="small">
            <strong>Description:</strong> {description}
          </Text>
          <Text variant="small">
            <strong>Current Value:</strong> {currentValue}
          </Text>
        </Stack>
      }
    >
      <Stack
        style={{
          padding: "6px 10px",
          borderRadius: 8,
          border: `1px solid ${color}20`,
          borderLeft: `4px solid ${color}`,
          minWidth: 100,
          cursor: onClick ? "pointer" : "default",
        }}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={onClick}
        onKeyDown={(ev) => {
          if (!onClick) return;
          if (ev.key === "Enter" || ev.key === " ") {
            ev.preventDefault();
            onClick();
          }
        }}
        tokens={{ childrenGap: 8 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
          <Text
            variant="small"
            style={{
              fontWeight: 600,
              fontSize: 13,
              color: "#323130",
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
              flex: 1,
            }}
          >
            {title}
          </Text>
          {showTooltipIcon && (
            <TooltipHost content={description || "Tooltip placeholder"}>
              <IconButton
                iconProps={{ iconName: "Info" }}
                styles={{
                  root: { background: "transparent", color: "#888", minWidth: 20, height: 20, padding: 0 },
                  rootHovered: { background: "transparent", color: "#333" }
                }}
                ariaLabel={`Info about ${title}`}
              />
            </TooltipHost>
          )}
        </div>
        <Text variant="large" style={{ fontWeight: 700, color }}>
          {abbrevatedValue ?? String(currentValue)}
        </Text>
          {!disableSparkline && (
        <div style={{ marginTop: "4px", marginBottom: "4px" }}>
          <Sparkline data={sampleData} width={140} height={30} showLegend={true} />
        </div>
          )}
      </Stack>
    </TooltipHost>
  );
};